import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';
import { PAGE_ROUTE } from '../lib/routes';
import { getLastLoginMethod, setLastLoginMethod, getSession, setSession, LastLoginMethod, getPendingIntent, clearPendingIntent } from '../lib/utils';
import { Eye, EyeSlash, FacebookLogo, GoogleLogo } from '@phosphor-icons/react';
interface LoginProps {
  navigate: (page: string, context?: any) => void;
  context?: {
    verified?: boolean;
    passwordReset?: boolean;
    error?: string;
  };
}
function LastUsedBadge() {
  return <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-semibold uppercase tracking-wider bg-lime-1 text-dark-1 px-2 py-0.5 rounded-full" data-id="element-3674">
      Ultima dată
    </span>;
}
export function Login({
  navigate,
  context
}: LoginProps) {
  const isVerified = context?.verified === true;
  const isPasswordReset = context?.passwordReset === true;
  const verifyError = context?.error;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastMethod, setLastMethod] = useState<LastLoginMethod | null>(null);
  useEffect(() => {
    setLastMethod(getLastLoginMethod());
  }, []);
  const persistSessionAndRoute = (loginMethod: LastLoginMethod) => {
    setLastLoginMethod(loginMethod);
    const existing = getSession();
    const role = existing?.role || 'demand';
    setSession({
      name: existing?.name || (email ? email.split('@')[0] : 'Utilizator'),
      email: email || existing?.email,
      phone: existing?.phone,
      avatar: existing?.avatar,
      role,
      hasListing: existing?.hasListing ?? true
    });
    // Consume any pending intent (auth gate from TenantAnunt etc.) and
    // bounce back to where the user came from with the right modal open.
    const intent = getPendingIntent();
    if (intent) {
      clearPendingIntent();
      navigate(PAGE_ROUTE.TENANT_ANUNT, {
        slug: intent.tenantSlug,
        openApplyModal: intent.type === 'send_offer'
      });
      return;
    }
    if (role === 'supply') {
      navigate(PAGE_ROUTE.MARKETPLACE);
    } else {
      navigate(PAGE_ROUTE.DASHBOARD_PROFIL);
    }
  };
  const handleCredentialsLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      persistSessionAndRoute('email');
    } catch {
      setError('A apărut o eroare. Te rugăm să încerci din nou.');
    } finally {
      setLoading(false);
    }
  };
  const handleSocial = (method: 'facebook' | 'google') => {
    persistSessionAndRoute(method);
  };
  return <div className="w-full max-w-md mx-auto px-4 py-12" data-id="element-3675">
      <div className="text-center mb-8" data-id="element-3676">
        <h1 className="text-2xl font-bold text-light-1" data-id="element-3677">Bine ai revenit</h1>
        <p className="text-sm text-light-3 mt-2" data-id="element-3678">
          Nu ai cont?{' '}
          <button onClick={() => navigate(PAGE_ROUTE.REGISTER)} className="font-medium text-lime-1 hover:text-lime-2 cursor-pointer" data-id="element-3679">
            Creează unul
          </button>
        </p>
      </div>

      {isVerified && <div className="mb-4 p-3 rounded-lg bg-lime-5 border border-lime-4 text-lime-1 text-sm text-center" data-id="element-3680">
          Email confirmat! Te poți autentifica acum.
        </div>}

      {isPasswordReset && <div className="mb-4 p-3 rounded-lg bg-lime-5 border border-lime-4 text-lime-1 text-sm text-center" data-id="element-3681">
          Parola a fost actualizată. Te poți autentifica acum.
        </div>}

      {verifyError === 'invalid-token' && <div className="mb-4 p-3 rounded-lg bg-red-4 border border-red-2 text-red-1 text-sm text-center" data-id="element-3682">
          Link de verificare invalid sau deja folosit.
        </div>}

      {verifyError === 'token-expired' && <div className="mb-4 p-3 rounded-lg bg-dark-3 border border-dark-4 text-light-1 text-sm text-center" data-id="element-3683">
          Link-ul de verificare a expirat. Te rugăm să creezi un cont din nou.
        </div>}

      <div className="space-y-4" data-id="element-3684">
        <div className="relative" data-id="element-3685">
          <Button onClick={() => handleSocial('facebook')} className="w-full h-12 bg-[#1877F2]/10 hover:bg-[#1877F2]/20 text-light-1 border border-[#1877F2]/30 relative" data-id="element-3686">
            <FacebookLogo weight="fill" className="w-5 h-5 mr-3 text-[#1877F2]" data-id="element-3687" />
            Continuă cu Facebook
          </Button>
          {lastMethod === 'facebook' && <LastUsedBadge data-id="element-3688" />}
        </div>

        <div className="relative" data-id="element-3689">
          <Button onClick={() => handleSocial('google')} className="w-full h-12 bg-dark-2 hover:bg-dark-3 text-light-1 border border-dark-3 relative" data-id="element-3690">
            <GoogleLogo weight="bold" className="w-5 h-5 mr-3" data-id="element-3691" />
            Continuă cu Google
          </Button>
          {lastMethod === 'google' && <LastUsedBadge data-id="element-3692" />}
        </div>

        <div className="relative py-4" data-id="element-3693">
          <div className="absolute inset-0 flex items-center" data-id="element-3694">
            <span className="w-full border-t border-dark-3" data-id="element-3695" />
          </div>
          <div className="relative flex justify-center text-xs uppercase" data-id="element-3696">
            <span className="bg-dark-1 px-2 text-light-3" data-id="element-3697">sau cu email</span>
          </div>
        </div>

        <form onSubmit={handleCredentialsLogin} className="space-y-4 relative" data-id="element-3698">
          {lastMethod === 'email' && <div className="absolute -top-2 right-0 z-10" data-id="element-3699">
              <span className="text-[10px] font-semibold uppercase tracking-wider bg-lime-1 text-dark-1 px-2 py-0.5 rounded-full" data-id="element-3700">
                Ultima dată
              </span>
            </div>}

          {error && <div className="p-3 rounded-lg bg-red-4 border border-red-2 text-red-1 text-sm" data-id="element-3701">
              {error}
            </div>}

          <Input type="email" placeholder="Adresa de email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" className="h-12 bg-dark-2 border-dark-3" data-id="element-3702" />

          <div className="relative" data-id="element-3703">
            <Input type={showPassword ? 'text' : 'password'} placeholder="Parola" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="current-password" className="h-12 bg-dark-2 border-dark-3 pr-12" data-id="element-3704" />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-light-3 hover:text-light-1 p-1" data-id="element-3705">
              {showPassword ? <EyeSlash className="w-5 h-5" data-id="element-3706" /> : <Eye className="w-5 h-5" data-id="element-3707" />}
            </button>
          </div>

          <div className="flex justify-end" data-id="element-3708">
            <button type="button" onClick={() => navigate(PAGE_ROUTE.RESET_PASSWORD)} className="text-xs text-lime-1 hover:text-lime-2" data-id="element-3709">
              Ai uitat parola?
            </button>
          </div>

          <Button type="submit" disabled={loading} className="w-full h-12 bg-lime-1 text-dark-1 hover:bg-lime-2 font-semibold text-base" data-id="element-3710">
            {loading ? <span className="flex items-center gap-2" data-id="element-3711">
                <Spinner size="sm" data-id="element-3712" />
                Se autentifică...
              </span> : 'Autentificare'}
          </Button>
        </form>
      </div>

      <p className="text-xs text-light-3 text-center leading-relaxed mt-8" data-id="element-3713">
        Continuând, confirmi că ai citit și ești de acord cu{' '}
        <button onClick={() => navigate(PAGE_ROUTE.TERMENI)} className="text-light-2 underline hover:text-lime-1" data-id="element-3714">
          Termenii și Condițiile
        </button>{' '}
        și{' '}
        <button onClick={() => navigate(PAGE_ROUTE.CONFIDENTIALITATE)} className="text-light-2 underline hover:text-lime-1" data-id="element-3715">
          Politica de Confidențialitate
        </button>
        .
      </p>
    </div>;
}